import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { DIRECTUS_CONFIG } from '../../config/directus';
import Grow from '@mui/material/Grow';

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
  language: 'fr' | 'en';
}

const texts = {
  fr: {
    title: 'Tenez-moi informé',
    error: "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer.",
    confirmTitle: 'Merci pour votre inscription !',
    confirmDesc: 'Nous vous tiendrons informé de nos actualités et de notre lancement.',
    info: 'Soyez parmi les premiers à découvrir Dingo et bénéficiez de nos offres exclusives.',
    cancel: 'Annuler',
    send: 'Envoyer',
    sending: 'Envoi en cours...'
  },
  en: {
    title: 'Keep me posted',
    error: 'An error occurred while sending your message. Please try again.',
    confirmTitle: 'Thank you for signing up!',
    confirmDesc: 'We will keep you updated on our news and launch.',
    info: 'Be among the first to discover Dingo and enjoy our exclusive offers.',
    cancel: 'Cancel',
    send: 'Send',
    sending: 'Sending...'
  }
};

const ContactDialog: React.FC<ContactDialogProps> = ({ open, onClose, language }) => {
  const t = texts[language] || texts.fr;
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        email: formData.email,
        firstname: formData.firstName,
        lastname: formData.lastName,
        message: formData.message,
        created_at: new Date().toISOString(),
        source: 'comingsoon_notifyme',
      };

      const response = await axios.post(
        `${DIRECTUS_CONFIG.url}${DIRECTUS_CONFIG.endpoints.contacts}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${DIRECTUS_CONFIG.token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setFormData({
            email: '',
            firstName: '',
            lastName: '',
            message: '',
          });
        }, 2000);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#4F46E5', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2
      }}>
        <Typography variant="inherit" sx={{ fontFamily: 'SF Pro Display', fontWeight: 600, fontSize: '1.2rem', color: 'white' }}>
          {t.title}
        </Typography>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {success ? (
        <DialogContent sx={{
          p: { xs: 2, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 220, sm: 260 },
        }}>
          <Grow in={success} timeout={500}>
            <CheckCircleIcon sx={{ fontSize: { xs: 56, sm: 72 }, color: '#10B981', mb: 2 }} />
          </Grow>
          <Typography variant="h6" sx={{ color: '#10B981', fontFamily: 'SF Pro Display', fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' }, mt: 1, textAlign: 'center' }}>
            {t.confirmTitle}
          </Typography>
          <Typography sx={{ color: '#6B7280', fontFamily: 'SF Pro Text', fontSize: { xs: '0.9rem', sm: '1rem' }, mt: 1, textAlign: 'center' }}>
            {t.confirmDesc}
          </Typography>
        </DialogContent>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              sx={{ 
                color: '#6B7280', 
                mb: 2, 
                fontFamily: 'SF Pro Text',
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                lineHeight: 1.5
              }}
            >
              {t.info}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: '#4F46E5',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4F46E5',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4F46E5',
                  },
                }}
              />
              <TextField
                required
                label="Prénom"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: '#4F46E5',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4F46E5',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4F46E5',
                  },
                }}
              />
              <TextField
                required
                label="Nom"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: '#4F46E5',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4F46E5',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4F46E5',
                  },
                }}
              />
              <TextField
                required
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: '#4F46E5',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4F46E5',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4F46E5',
                  },
                }}
              />
              {error && (
                <Typography sx={{ color: '#EF4444', mt: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  {t.error}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
            <Button 
              onClick={onClose}
              sx={{
                color: '#6B7280',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  bgcolor: 'rgba(107, 114, 128, 0.1)',
                },
              }}
            >
              {t.cancel}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#4F46E5',
                color: 'white',
                px: { xs: 3, sm: 4 },
                py: 1,
                borderRadius: '9999px',
                textTransform: 'none',
                fontFamily: 'SF Pro Text',
                fontWeight: 500,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  bgcolor: '#4338CA',
                },
                '&:disabled': {
                  bgcolor: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            >
              {loading ? t.sending : t.send}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default ContactDialog; 